import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { useTranslation } from 'react-i18next';
import { reportApi, paymentApi, expensePaymentApi, dashboardApi } from '../services/api';
import { formatDate, formatDateRange } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';

// Register Chart.js components
Chart.register(...registerables);

const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [cachedData, setCachedData] = useState(null);
  const [isLoadingRealData, setIsLoadingRealData] = useState(false);

  // First, load cached data for immediate display
  useEffect(() => {
    const fetchCachedData = async () => {
      try {
        const cacheResponse = await dashboardApi.getCache();
        if (cacheResponse.data && cacheResponse.data.total_monthly_income !== undefined) {
          setCachedData(cacheResponse.data);
          
          // Create a minimal report object from cached data
          setReport({
            total_monthly_income: cacheResponse.data.total_monthly_income,
            total_monthly_expenses: cacheResponse.data.total_monthly_expenses,
            monthly_balance: cacheResponse.data.monthly_balance,
            member_summaries: new Array(cacheResponse.data.member_count || 0),
            expense_summaries: [],
            monthly_summaries: []
          });
          
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to load cached dashboard data:", err);
        // Don't set error here, as we'll try to load real data next
      }
    };

    fetchCachedData();
  }, []);

  // Then, load the real data
  useEffect(() => {
    const fetchRealData = async () => {
      try {
        setIsLoadingRealData(true);
        const response = await reportApi.getFinancialReport();
        setReport(response.data);
        
        // Get all payments and expense payments to calculate actual monthly income and expenses
        const paymentsResponse = await paymentApi.getAll();
        const allPayments = paymentsResponse.data;
        
        const expensePaymentsResponse = await expensePaymentApi.getAll();
        const allExpensePayments = expensePaymentsResponse.data;
        
        // Only update the cache if the user is an owner or admin
        if (hasRole('owner') || hasRole('admin')) {
          try {
            await dashboardApi.updateCache({
              total_monthly_income: response.data.total_monthly_income,
              total_monthly_expenses: response.data.total_monthly_expenses,
              monthly_balance: response.data.monthly_balance,
              member_count: response.data.member_summaries.length,
              last_updated: new Date().toISOString()
            });
          } catch (cacheErr) {
            console.error("Failed to update dashboard cache:", cacheErr);
            // Continue with rendering the dashboard even if cache update fails
          }
        }
        
        // Prepare chart data
        if (response.data.monthly_summaries && response.data.monthly_summaries.length > 0) {
          const labels = response.data.monthly_summaries.map(summary => summary.month);
          
          // Calculate actual income based on payment date (paid_on)
          const actualIncomeData = labels.map(monthStr => {
            const [year, month] = monthStr.split('-').map(Number);
            const startDate = new Date(year, month - 1, 1); // Month is 0-indexed in JS Date
            const endDate = new Date(year, month, 0); // Last day of the month
            
            // Filter payments for this month based on paid_on date
            const monthlyPayments = allPayments.filter(payment => {
              const paymentDate = new Date(payment.paid_on);
              return paymentDate >= startDate && paymentDate <= endDate;
            });
            
            // Sum the amounts
            return monthlyPayments.reduce((sum, payment) => sum + payment.amount, 0);
          });
          
          // Calculate actual expenses based on payment date (paid_on)
          const actualExpenseData = labels.map(monthStr => {
            const [year, month] = monthStr.split('-').map(Number);
            const startDate = new Date(year, month - 1, 1); // Month is 0-indexed in JS Date
            const endDate = new Date(year, month, 0); // Last day of the month
            
            // Filter expense payments for this month based on paid_on date
            const monthlyExpensePayments = allExpensePayments.filter(payment => {
              const paymentDate = new Date(payment.paid_on);
              return paymentDate >= startDate && paymentDate <= endDate;
            });
            
            // Sum the amounts
            return monthlyExpensePayments.reduce((sum, payment) => sum + payment.amount, 0);
          });
          
          // Calculate balance as actual income - actual expenses
          const balanceData = actualIncomeData.map((income, index) => income - actualExpenseData[index]);
          
          // Add expected income and expense data (constant values for all months)
          const expectedIncomeData = response.data.monthly_summaries.map(() => response.data.total_monthly_income);
          const expectedExpenseData = response.data.monthly_summaries.map(() => response.data.total_monthly_expenses);
          
          setChartData({
            labels,
            datasets: [
              {
                label: 'Actual Income',
                data: actualIncomeData,
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
              },
              {
                label: 'Expected Income',
                data: expectedIncomeData,
                backgroundColor: 'rgba(153, 102, 255, 0.2)',
                borderColor: 'rgba(153, 102, 255, 1)',
                borderWidth: 1,
                type: 'line',
                pointRadius: 0,
                borderDash: [5, 5],
              },
              {
                label: 'Actual Expenses',
                data: actualExpenseData,
                backgroundColor: 'rgba(255, 99, 132, 0.2)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1,
              },
              {
                label: 'Expected Expenses',
                data: expectedExpenseData,
                backgroundColor: 'rgba(255, 159, 64, 0.2)',
                borderColor: 'rgba(255, 159, 64, 1)',
                borderWidth: 1,
                type: 'line',
                pointRadius: 0,
                borderDash: [5, 5],
              },
              {
                label: 'Balance',
                data: balanceData,
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
              },
            ],
          });
        }
        
        setIsLoadingRealData(false);
        setLoading(false);
      } catch (err) {
        setError('Failed to load dashboard data');
        setIsLoadingRealData(false);
        
        // Only set loading to false if we don't have cached data
        if (!cachedData) {
          setLoading(false);
          
          // Set default empty report data as fallback only if no cached data
          setReport({
            total_monthly_income: 0,
            total_monthly_expenses: 0,
            monthly_balance: 0,
            member_summaries: [],
            expense_summaries: [],
            monthly_summaries: []
          });
        }
        
        console.error(err);
      }
    };

    // Load real data after cached data is displayed or if no cached data
    if (!loading || cachedData) {
      fetchRealData();
    }
  }, [loading, cachedData, hasRole]);

  if (loading && !cachedData) {
    return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  }

  if (error) {
    return <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>;
  }

  if (!report) {
    return <div className="alert alert-warning">{t('common:messages.noData')}</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5 mb-4">{t('dashboard:title')}</h1>
        </div>
      </div>
      
      <div className="row mb-4">
        <div className="col-md-3 mb-3 mb-md-0">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center p-4">
              <h3 className="text-muted mb-3">{t('dashboard:summary.monthlyIncome')}</h3>
              <div className="display-6 fw-bold text-success">
                {report.total_monthly_income.toFixed(2)} €
              </div>
              {isLoadingRealData && cachedData && (
                <div className="mt-2 small text-muted">
                  <i className="fas fa-sync fa-spin me-1"></i> {t('dashboard:cacheStatus.loading')}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3 mb-md-0">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center p-4">
              <h3 className="text-muted mb-3">{t('dashboard:summary.monthlyExpenses')}</h3>
              <div className="display-6 fw-bold text-danger">
                {report.total_monthly_expenses.toFixed(2)} €
              </div>
              {isLoadingRealData && cachedData && (
                <div className="mt-2 small text-muted">
                  <i className="fas fa-sync fa-spin me-1"></i> {t('dashboard:cacheStatus.loading')}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3 mb-md-0">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center p-4">
              <h3 className="text-muted mb-3">{t('dashboard:summary.monthlyBalance')}</h3>
              <div className={`display-6 fw-bold ${report.monthly_balance >= 0 ? 'text-primary' : 'text-danger'}`}>
                {report.monthly_balance.toFixed(2)} €
              </div>
              {isLoadingRealData && cachedData && (
                <div className="mt-2 small text-muted">
                  <i className="fas fa-sync fa-spin me-1"></i> {t('dashboard:cacheStatus.loading')}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-body text-center p-4">
              <h3 className="text-muted mb-3">{t('dashboard:summary.memberCount')}</h3>
              <div className="display-6 fw-bold text-primary">
                {report.member_summaries.length}
              </div>
              {isLoadingRealData && cachedData && (
                <div className="mt-2 small text-muted">
                  <i className="fas fa-sync fa-spin me-1"></i> {t('dashboard:cacheStatus.loading')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {chartData ? (
        <div className="row mb-4">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-bar me-2"></i>
                  {t('dashboard:expenseBreakdown.title')}
                </h5>
              </div>
              <div className="card-body">
                <div style={{ height: '350px' }}>
                  <Bar 
                    data={chartData} 
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : isLoadingRealData && (
        <div className="row mb-4">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-bar me-2"></i>
                  Financial Overview (Last 12 Months)
                </h5>
              </div>
              <div className="card-body">
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x"></i>
                  <p className="mt-2">{t('dashboard:cacheStatus.loading')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {report.member_summaries && report.member_summaries.length > 0 ? (
        <div className="row mb-4">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="card-title mb-0">
                  <i className="fas fa-users me-2"></i>
                  {t('dashboard:memberStatus.title')}
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead >
                      <tr>
                        <th>{t('common:navigation.users')}</th>
                        <th>{t('dashboard:memberStatus.currentFee')}</th>
                        <th>{t('dashboard:memberStatus.paidUntil')}</th>
                        <th>{t('dashboard:memberStatus.monthsInAdvance')}</th>
                        <th>{t('dashboard:memberStatus.missingMonths')}</th>
                        <th className="text-end" style={{ width: '1%', whiteSpace: 'nowrap' }}>{t('common:actions.view')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.member_summaries
                        .filter(member => member.current_fee > 0) // Filter out free members
                        .map(member => (
                        <tr key={member.member_id}>
                          <td className="fw-bold">{member.member_name}</td>
                          <td>{member.current_fee.toFixed(2)} €</td>
                          <td>{member.paid_until ? formatDate(member.paid_until) : 'N/A'}</td>
                          <td>
                            <span className={`badge ${
                              member.months_paid_in_advance > 2 ? 'bg-success' : 
                              member.months_paid_in_advance > 0 ? 'bg-warning text-dark' : 
                              'bg-danger'
                            }`}>
                              {member.months_paid_in_advance}
                            </span>
                          </td>
                          <td>
                            {member.missing_months && member.missing_months.length > 0 ? (
                              <span className="badge bg-danger">{member.missing_months.join(', ')}</span>
                            ) : (
                              <span className="badge bg-success">{t('dashboard:memberStatus.none')}</span>
                            )}
                          </td>
                          <td className="text-end">
                            <Link to={`/members/${member.member_id}`} className="btn btn-sm btn-outline-primary">
                              <i className="fas fa-eye"></i>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : isLoadingRealData && (
        <div className="row mb-4">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="card-title mb-0">
                  <i className="fas fa-users me-2"></i>
                  Member Payment Status
                </h5>
              </div>
              <div className="card-body">
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x"></i>
                  <p className="mt-2">{t('dashboard:cacheStatus.loading')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {report.expense_summaries && report.expense_summaries.length > 0 ? (
        <div className="row mb-4">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="card-title mb-0">
                  <i className="fas fa-file-invoice-dollar me-2"></i>
                  {t('dashboard:expenseStatus.title')}
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead >
                      <tr>
                        <th>{t('dashboard:expenseStatus.name')}</th>
                        <th>{t('dashboard:expenseStatus.category')}</th>
                        <th>{t('dashboard:expenseStatus.interval')}</th>
                        <th>{t('dashboard:expenseStatus.monthlyCost')}</th>
                        <th>{t('dashboard:expenseStatus.paidUntil')}</th>
                        <th className="text-end" style={{ width: '1%', whiteSpace: 'nowrap' }}>{t('common:actions.view')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.expense_summaries.map(expense => (
                        <tr key={expense.expense_id}>
                          <td className="fw-bold">{expense.expense_name}</td>
                          <td>
                            <span className="badge bg-secondary rounded-pill">
                              {expense.category}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              expense.interval === 'monthly' ? 'bg-primary' : 
                              expense.interval === 'quarterly' ? 'bg-info' : 
                              expense.interval === 'yearly' ? 'bg-warning text-dark' : 
                              'bg-dark'
                            }`}>
                              {expense.interval}
                            </span>
                          </td>
                          <td>{expense.monthly_cost.toFixed(2)} €</td>
                          <td>{expense.paid_until ? formatDate(expense.paid_until) : 'N/A'}</td>
                          <td className="text-end">
                            <Link to={`/expenses/${expense.expense_id}`} className="btn btn-sm btn-outline-primary">
                              <i className="fas fa-eye"></i>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : isLoadingRealData && (
        <div className="row mb-4">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="card-title mb-0">
                  <i className="fas fa-file-invoice-dollar me-2"></i>
                  Expense Status
                </h5>
              </div>
              <div className="card-body">
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x"></i>
                  <p className="mt-2">{t('dashboard:cacheStatus.loading')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
